import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { MarcaDTO } from '../../../models/marca.model';
import { MarcaService } from '../../../services/marca/marca.service';

@Component({
  selector: 'app-marca-adicionar-dialog',
  templateUrl: './marca-adicionar-dialog.component.html',
  styleUrls: ['./marca-adicionar-dialog.component.scss'],
})
export class MarcaAdicionarDialogComponent implements OnChanges, OnInit, OnDestroy {

  // === INPUTS ===
  @Input() visible = false;

  // === OUTPUTS ===
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() marcaCriada = new EventEmitter<MarcaDTO>();

  // === ESTADO INTERNO ===
  nome: string = '';
  imagemBase64: string | null = null;
  imagemNome: string = '';
  salvando: boolean = false;

  // === TRADUCOES ===
  texts: {
    titulo: string;
    subtitulo: string;
    labels_nome: string;
    labels_imagem: string;
    placeholders_nome_da_marca: string;
    botoes_fechar: string;
    botoes_guardar: string;
    botoes_escolher_imagem: string;
    mensagens_campos_obrigatorios: string;
    mensagens_marca_cadastrada: string;
    mensagens_erro_cadastrar: string;
  } = {
    titulo: '',
    subtitulo: '',
    labels_nome: '',
    labels_imagem: '',
    placeholders_nome_da_marca: '',
    botoes_fechar: '',
    botoes_guardar: '',
    botoes_escolher_imagem: '',
    mensagens_campos_obrigatorios: '',
    mensagens_marca_cadastrada: '',
    mensagens_erro_cadastrar: '',
  };

  private langSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private marcaService: MarcaService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetar();
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  // === METODOS PUBLICOS ===

  /** Converte arquivo de imagem para base64 */
  onImageSelect(event: any): void {
    const file = event.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // Extrair apenas o base64 (sem o prefixo data:image/...)
      const result = reader.result as string;
      this.imagemBase64 = result.split(',')[1] || result;
      this.imagemNome = file.name;
    };
    reader.readAsDataURL(file);
  }

  /** Limpa imagem selecionada */
  onImageClear(): void {
    this.imagemBase64 = null;
    this.imagemNome = '';
  }

  /** Submete o formulario de nova marca */
  submit(): void {
    if (!this.nome || !this.nome.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.mensagens_campos_obrigatorios,
      });
      return;
    }

    this.salvando = true;

    const body: MarcaDTO = {
      nome: this.nome.trim(),
      imagem: this.imagemBase64 || undefined,
    };

    this.marcaService.post('/marca', body).subscribe({
      next: (marcaCriada: MarcaDTO) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.mensagens_marca_cadastrada,
        });
        this.marcaCriada.emit(marcaCriada);
        this.resetar();
        this.fechar();
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_cadastrar,
        });
      },
    });
  }

  /** Fecha o dialog */
  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // === METODOS PRIVADOS ===

  /** Limpa o estado do formulario */
  private resetar(): void {
    this.nome = '';
    this.imagemBase64 = null;
    this.imagemNome = '';
    this.salvando = false;
  }

  /** Carrega traducoes do namespace select-marca.dialog */
  private loadTranslations(): void {
    const prefix = 'select-marca.dialog';
    this.texts = {
      titulo: this.translate.instant(`${prefix}.titulo`),
      subtitulo: this.translate.instant(`${prefix}.subtitulo`),
      labels_nome: this.translate.instant(`${prefix}.labels.nome`),
      labels_imagem: this.translate.instant(`${prefix}.labels.imagem`),
      placeholders_nome_da_marca: this.translate.instant(`${prefix}.placeholders.nome_da_marca`),
      botoes_fechar: this.translate.instant(`${prefix}.botoes.fechar`),
      botoes_guardar: this.translate.instant(`${prefix}.botoes.guardar`),
      botoes_escolher_imagem: this.translate.instant(`${prefix}.botoes.escolher_imagem`),
      mensagens_campos_obrigatorios: this.translate.instant(`${prefix}.mensagens.campos_obrigatorios`),
      mensagens_marca_cadastrada: this.translate.instant(`${prefix}.mensagens.marca_cadastrada`),
      mensagens_erro_cadastrar: this.translate.instant(`${prefix}.mensagens.erro_cadastrar`),
    };
  }
}
